import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { Group, Mesh } from 'three';
import type { ModelProps, ModelRegistry } from '../types';

// ==================== LE (R)ÉVEIL DE L'ÉDUCATRICE MODELS ====================

/**
 * L'Oracle Chevelu - Benours déguisé en sage mystérieux
 *
 * Apparence:
 * - Robe de sage dorée et majestueuse
 * - Chevelure MAGNIFIQUE : longue, ondulée, brillante, qui flotte au vent
 * - Barbe également fournie
 * - Aura mystique
 */
function OracleCheveluModel({ world, hovered }: ModelProps) {
  const isSpectral = world === 'spectral';
  const robeColor = isSpectral ? '#b8860b' : '#ffd700';
  const robeAccent = isSpectral ? '#8b6914' : '#daa520';
  const hairColor = isSpectral ? '#8b7355' : '#d2691e';
  const skinColor = isSpectral ? '#d8c8b8' : '#ffe4c4';
  const auraColor = isSpectral ? '#9370db' : '#ffd700';

  return (
    <group>
      {/* Hitbox invisible pour faciliter le clic */}
      <mesh position={[0, 0.6, 0]}>
        <boxGeometry args={[1.5, 1.6, 1.5]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* === BASE / FEET === */}
      <mesh position={[0, 0.05, 0]} castShadow>
        <cylinderGeometry args={[0.2, 0.25, 0.1, 16]} />
        <meshStandardMaterial color={robeColor} />
      </mesh>

      {/* === ROBE MAJESTUEUSE === */}
      {/* Base de la robe - très large */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <coneGeometry args={[0.35, 0.6, 16]} />
        <meshStandardMaterial color={robeColor} />
      </mesh>

      {/* Ornements dorés sur la robe */}
      {[0.15, 0.25, 0.35].map((y, i) => (
        <mesh key={i} position={[0, y, 0]} rotation={[Math.PI / 2, 0, 0]} castShadow>
          <torusGeometry args={[0.32 - i * 0.05, 0.01, 8, 32]} />
          <meshStandardMaterial color={robeAccent} metalness={0.7} roughness={0.3} />
        </mesh>
      ))}

      {/* Torse */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.2, 0.25, 12]} />
        <meshStandardMaterial color={robeColor} />
      </mesh>

      {/* Col brodé */}
      <mesh position={[0, 0.72, 0]} castShadow>
        <torusGeometry args={[0.12, 0.03, 8, 16]} />
        <meshStandardMaterial color={robeAccent} metalness={0.6} roughness={0.4} />
      </mesh>

      {/* === BRAS === */}
      {/* Manches amples */}
      <mesh position={[-0.22, 0.55, 0]} rotation={[0, 0, 0.5]} castShadow>
        <coneGeometry args={[0.1, 0.25, 8]} />
        <meshStandardMaterial color={robeColor} />
      </mesh>
      <mesh position={[0.22, 0.55, 0]} rotation={[0, 0, -0.5]} castShadow>
        <coneGeometry args={[0.1, 0.25, 8]} />
        <meshStandardMaterial color={robeColor} />
      </mesh>

      {/* Mains */}
      <mesh position={[-0.32, 0.48, 0]} castShadow>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      <mesh position={[0.32, 0.48, 0]} castShadow>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>

      {/* === TÊTE === */}
      <mesh position={[0, 0.85, 0]} castShadow>
        <sphereGeometry args={[0.12, 16, 16]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>

      {/* === LA MAGNIFIQUE CHEVELURE === */}
      {/* Cheveux principaux - ondulés et volumineux */}
      <mesh position={[0, 0.9, -0.05]} castShadow>
        <sphereGeometry args={[0.14, 16, 16, 0, Math.PI * 2, 0, Math.PI / 1.5]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>

      {/* Mèches latérales qui descendent */}
      <mesh position={[-0.12, 0.75, -0.02]} castShadow>
        <capsuleGeometry args={[0.04, 0.25, 8, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      <mesh position={[0.12, 0.75, -0.02]} castShadow>
        <capsuleGeometry args={[0.04, 0.25, 8, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>

      {/* Cheveux qui flottent vers l'arrière */}
      <mesh position={[-0.08, 0.8, -0.12]} rotation={[0.3, 0, -0.2]} castShadow>
        <capsuleGeometry args={[0.035, 0.2, 8, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      <mesh position={[0.08, 0.8, -0.12]} rotation={[0.3, 0, 0.2]} castShadow>
        <capsuleGeometry args={[0.035, 0.2, 8, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      <mesh position={[0, 0.78, -0.15]} rotation={[0.4, 0, 0]} castShadow>
        <capsuleGeometry args={[0.04, 0.22, 8, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>

      {/* === LA MAGNIFIQUE BARBE === */}
      {/* Barbe principale */}
      <mesh position={[0, 0.68, 0.08]} castShadow>
        <coneGeometry args={[0.08, 0.25, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>

      {/* Moustache */}
      <mesh position={[-0.04, 0.78, 0.1]} rotation={[0, 0, 0.3]} castShadow>
        <capsuleGeometry args={[0.015, 0.06, 6, 6]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      <mesh position={[0.04, 0.78, 0.1]} rotation={[0, 0, -0.3]} castShadow>
        <capsuleGeometry args={[0.015, 0.06, 6, 6]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>

      {/* === YEUX SAGES === */}
      {/* Yeux blancs */}
      <mesh position={[-0.04, 0.87, 0.1]} castShadow>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0.04, 0.87, 0.1]} castShadow>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>

      {/* Pupilles (marron sage) */}
      <mesh position={[-0.04, 0.87, 0.12]} castShadow>
        <sphereGeometry args={[0.012, 8, 8]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>
      <mesh position={[0.04, 0.87, 0.12]} castShadow>
        <sphereGeometry args={[0.012, 8, 8]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>

      {/* Sourcils épais */}
      <mesh position={[-0.04, 0.91, 0.1]} rotation={[0, 0, 0.1]} castShadow>
        <boxGeometry args={[0.04, 0.015, 0.02]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      <mesh position={[0.04, 0.91, 0.1]} rotation={[0, 0, -0.1]} castShadow>
        <boxGeometry args={[0.04, 0.015, 0.02]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>

      {/* Nez distingué */}
      <mesh position={[0, 0.83, 0.12]} rotation={[0.3, 0, 0]} castShadow>
        <coneGeometry args={[0.02, 0.05, 6]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>

      {/* === ACCESSOIRES === */}
      {/* Bâton de sage */}
      <mesh position={[0.4, 0.5, 0]} castShadow>
        <cylinderGeometry args={[0.02, 0.025, 1.0, 8]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>

      {/* Orbe mystique au sommet du bâton */}
      <mesh position={[0.4, 1.05, 0]} castShadow>
        <sphereGeometry args={[0.06, 12, 12]} />
        <meshStandardMaterial
          color={auraColor}
          emissive={auraColor}
          emissiveIntensity={0.5}
          transparent
          opacity={0.9}
        />
      </mesh>

      {/* Médaillon */}
      <mesh position={[0, 0.65, 0.12]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.01, 12]} />
        <meshStandardMaterial color="#ffd700" metalness={0.9} roughness={0.1} />
      </mesh>
      <mesh position={[0, 0.65, 0.125]} castShadow>
        <sphereGeometry args={[0.015, 8, 8]} />
        <meshStandardMaterial
          color="#ff6b6b"
          emissive="#ff6b6b"
          emissiveIntensity={0.3}
        />
      </mesh>

      {/* === AURA MYSTIQUE === */}
      {/* Cercle d'aura au sol */}
      <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.3, 0.35, 32]} />
        <meshBasicMaterial color={auraColor} transparent opacity={0.3} />
      </mesh>

      {/* Particules d'aura flottantes */}
      {[0, 1, 2, 3].map((i) => (
        <mesh
          key={i}
          position={[
            Math.cos((i / 4) * Math.PI * 2) * 0.3,
            0.3 + i * 0.15,
            Math.sin((i / 4) * Math.PI * 2) * 0.3,
          ]}
          castShadow
        >
          <sphereGeometry args={[0.02, 6, 6]} />
          <meshStandardMaterial
            color={auraColor}
            emissive={auraColor}
            emissiveIntensity={0.5}
            transparent
            opacity={0.7}
          />
        </mesh>
      ))}

      {/* === HOVER EFFECT === */}
      {hovered && (
        <pointLight
          position={[0, 0.8, 0.2]}
          intensity={1}
          color={auraColor}
          distance={3}
        />
      )}
    </group>
  );
}

/**
 * Portail Générique - Utilisé pour les différents mondes
 */
function PortalModel({ world, hovered, properties }: ModelProps) {
  const portalColor = (properties.portalColor as string) || '#9370db';
  const isActive = properties.isActive as boolean;
  const activeColor = (properties.activeColor as string) || portalColor;
  const color = isActive ? activeColor : portalColor;
  const glowIntensity = isActive ? 1.0 : 0.3;

  return (
    <group>
      {/* Frame du portail */}
      <mesh position={[0, 0.5, 0]} castShadow>
        <torusGeometry args={[0.45, 0.08, 8, 32]} />
        <meshStandardMaterial
          color={world === 'spectral' ? '#3a3a5a' : '#5a5a7a'}
          metalness={0.6}
          roughness={0.4}
        />
      </mesh>

      {/* Surface du portail */}
      <mesh position={[0, 0.5, 0]}>
        <circleGeometry args={[0.42, 32]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={glowIntensity}
          transparent
          opacity={isActive ? 0.8 : 0.3}
          side={2}
        />
      </mesh>

      {/* Effet de lueur quand actif */}
      {isActive && (
        <>
          <mesh position={[0, 0.5, 0.01]}>
            <ringGeometry args={[0.35, 0.42, 32]} />
            <meshBasicMaterial
              color={activeColor}
              transparent
              opacity={0.5}
            />
          </mesh>
          {hovered && (
            <pointLight position={[0, 0.5, 0.5]} intensity={1} color={activeColor} distance={3} />
          )}
        </>
      )}

      {/* Symboles sur le cadre */}
      {[0, 1, 2, 3].map((i) => (
        <mesh
          key={i}
          position={[
            Math.cos((i / 4) * Math.PI * 2 + Math.PI / 4) * 0.5,
            0.5 + Math.sin((i / 4) * Math.PI * 2 + Math.PI / 4) * 0.5,
            0.1
          ]}
        >
          <sphereGeometry args={[0.04, 8, 8]} />
          <meshStandardMaterial
            color={color}
            emissive={color}
            emissiveIntensity={isActive ? 0.8 : 0.2}
          />
        </mesh>
      ))}
    </group>
  );
}

/**
 * Pilier de cristal - Décoratif
 */
function PillarModel({ world, properties }: ModelProps) {
  const color = (properties.color as string) || '#9370db';
  const glows = properties.glows as boolean;
  const isSpectral = world === 'spectral';

  return (
    <group>
      {/* Base */}
      <mesh position={[0, 0.1, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.2, 0.2, 8]} />
        <meshStandardMaterial
          color={isSpectral ? '#3a3a5a' : '#6a6a8a'}
          metalness={0.5}
          roughness={0.5}
        />
      </mesh>

      {/* Corps du pilier */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.12, 0.8, 6]} />
        <meshStandardMaterial
          color={color}
          emissive={glows ? color : undefined}
          emissiveIntensity={glows ? 0.3 : 0}
          transparent
          opacity={0.9}
        />
      </mesh>

      {/* Sommet */}
      <mesh position={[0, 1.1, 0]} castShadow>
        <coneGeometry args={[0.08, 0.2, 6]} />
        <meshStandardMaterial
          color={color}
          emissive={glows ? color : undefined}
          emissiveIntensity={glows ? 0.5 : 0}
        />
      </mesh>
    </group>
  );
}

/**
 * Orbe lumineux flottant
 */
function OrbModel({ properties }: ModelProps) {
  const color = (properties.color as string) || '#ffffff';
  const glows = properties.glows as boolean;

  return (
    <mesh castShadow>
      <sphereGeometry args={[0.5, 16, 16]} />
      <meshStandardMaterial
        color={color}
        emissive={glows ? color : undefined}
        emissiveIntensity={glows ? 0.8 : 0}
        transparent
        opacity={0.9}
      />
    </mesh>
  );
}

/**
 * Cristal flottant
 */
function CrystalModel({ world, properties }: ModelProps) {
  const isSpectral = world === 'spectral';
  const baseColor = isSpectral ? '#6a5acd' : '#9370db';
  const glows = properties.glows as boolean;

  return (
    <group>
      {/* Corps du cristal - forme octaédrique */}
      <mesh castShadow>
        <octahedronGeometry args={[0.3, 0]} />
        <meshStandardMaterial
          color={baseColor}
          emissive={glows ? baseColor : undefined}
          emissiveIntensity={glows ? 0.4 : 0}
          transparent
          opacity={0.85}
        />
      </mesh>

      {/* Petit cristal secondaire */}
      <mesh position={[0.15, 0.1, 0]} rotation={[0.3, 0.5, 0]} castShadow>
        <octahedronGeometry args={[0.12, 0]} />
        <meshStandardMaterial
          color={baseColor}
          emissive={glows ? baseColor : undefined}
          emissiveIntensity={glows ? 0.3 : 0}
          transparent
          opacity={0.8}
        />
      </mesh>
    </group>
  );
}

/**
 * Plateforme cristalline
 */
function PlatformCrystalModel({ world }: ModelProps) {
  const isSpectral = world === 'spectral';
  const color = isSpectral ? '#4a4a6a' : '#7a7a9a';

  return (
    <group>
      {/* Surface principale */}
      <mesh position={[0, 0.1, 0]} receiveShadow castShadow>
        <cylinderGeometry args={[0.5, 0.55, 0.2, 8]} />
        <meshStandardMaterial
          color={color}
          metalness={0.4}
          roughness={0.6}
        />
      </mesh>

      {/* Motifs lumineux */}
      <mesh position={[0, 0.21, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.2, 0.25, 8]} />
        <meshStandardMaterial
          color="#9370db"
          emissive="#9370db"
          emissiveIntensity={0.3}
          transparent
          opacity={0.6}
        />
      </mesh>
    </group>
  );
}

/**
 * Coussin doré pour l'Oracle
 */
function CushionGoldModel({ world }: ModelProps) {
  const isSpectral = world === 'spectral';
  const color = isSpectral ? '#8b6914' : '#daa520';

  return (
    <group>
      {/* Coussin principal */}
      <mesh position={[0, 0.1, 0]} castShadow>
        <cylinderGeometry args={[0.45, 0.5, 0.15, 16]} />
        <meshStandardMaterial color={color} />
      </mesh>

      {/* Décoration */}
      <mesh position={[0, 0.18, 0]}>
        <torusGeometry args={[0.35, 0.02, 8, 32]} />
        <meshStandardMaterial
          color="#ffd700"
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>
    </group>
  );
}

/**
 * Pierre Ancestrale - Low poly détaillée
 * Un monolithe ancien avec des runes et un parchemin gravé
 */
function AncientStoneModel({ world, hovered }: ModelProps) {
  const isSpectral = world === 'spectral';
  const stoneColor = isSpectral ? '#4a4a5a' : '#6a6a7a';
  const runeColor = isSpectral ? '#9370db' : '#daa520';
  const glowIntensity = hovered ? 0.6 : 0.3;

  return (
    <group>
      {/* Hitbox invisible pour faciliter le clic - exactement comme PettableNPC */}
      <mesh position={[0, 0.8, 0]}>
        <boxGeometry args={[1.5, 2.0, 1.5]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* Base de la pierre */}
      <mesh position={[0, 0.1, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.5, 0.6, 0.2, 8]} />
        <meshStandardMaterial color={stoneColor} roughness={0.9} />
      </mesh>

      {/* Corps principal du monolithe - forme irrégulière low poly */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.35, 0.45, 0.8, 6]} />
        <meshStandardMaterial color={stoneColor} roughness={0.85} />
      </mesh>

      {/* Sommet pointu */}
      <mesh position={[0, 1.15, 0]} castShadow>
        <coneGeometry args={[0.32, 0.5, 6]} />
        <meshStandardMaterial color={stoneColor} roughness={0.85} />
      </mesh>

      {/* Runes gravées sur la face avant */}
      {/* Rune 1 - Triangle */}
      <mesh position={[0, 0.9, 0.36]} rotation={[0, 0, 0]}>
        <coneGeometry args={[0.06, 0.12, 3]} />
        <meshStandardMaterial
          color={runeColor}
          emissive={runeColor}
          emissiveIntensity={glowIntensity}
        />
      </mesh>

      {/* Rune 2 - Cercle */}
      <mesh position={[0, 0.65, 0.37]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.05, 0.015, 8, 16]} />
        <meshStandardMaterial
          color={runeColor}
          emissive={runeColor}
          emissiveIntensity={glowIntensity}
        />
      </mesh>

      {/* Rune 3 - Ligne verticale */}
      <mesh position={[0, 0.45, 0.38]}>
        <boxGeometry args={[0.02, 0.15, 0.01]} />
        <meshStandardMaterial
          color={runeColor}
          emissive={runeColor}
          emissiveIntensity={glowIntensity}
        />
      </mesh>

      {/* Rune 4 - Losange */}
      <mesh position={[-0.15, 0.75, 0.35]} rotation={[0, 0, Math.PI / 4]}>
        <boxGeometry args={[0.05, 0.05, 0.01]} />
        <meshStandardMaterial
          color={runeColor}
          emissive={runeColor}
          emissiveIntensity={glowIntensity}
        />
      </mesh>

      {/* Rune 5 - Losange */}
      <mesh position={[0.15, 0.75, 0.35]} rotation={[0, 0, Math.PI / 4]}>
        <boxGeometry args={[0.05, 0.05, 0.01]} />
        <meshStandardMaterial
          color={runeColor}
          emissive={runeColor}
          emissiveIntensity={glowIntensity}
        />
      </mesh>

      {/* Zone du parchemin gravé (rectangle central) */}
      <mesh position={[0, 0.55, 0.36]}>
        <boxGeometry args={[0.25, 0.35, 0.02]} />
        <meshStandardMaterial
          color={isSpectral ? '#3a3a4a' : '#5a5a6a'}
          roughness={0.7}
        />
      </mesh>

      {/* Lignes de texte sur le parchemin */}
      {[0.68, 0.60, 0.52, 0.44].map((y, i) => (
        <mesh key={i} position={[0, y, 0.375]}>
          <boxGeometry args={[0.18 - i * 0.02, 0.015, 0.005]} />
          <meshStandardMaterial
            color={runeColor}
            emissive={runeColor}
            emissiveIntensity={glowIntensity * 0.5}
          />
        </mesh>
      ))}

      {/* Petites pierres décoratives à la base */}
      <mesh position={[0.4, 0.08, 0.2]} castShadow>
        <dodecahedronGeometry args={[0.08, 0]} />
        <meshStandardMaterial color={stoneColor} roughness={0.9} />
      </mesh>
      <mesh position={[-0.35, 0.06, 0.3]} castShadow>
        <dodecahedronGeometry args={[0.06, 0]} />
        <meshStandardMaterial color={stoneColor} roughness={0.9} />
      </mesh>
      <mesh position={[0.3, 0.07, -0.25]} castShadow>
        <dodecahedronGeometry args={[0.07, 0]} />
        <meshStandardMaterial color={stoneColor} roughness={0.9} />
      </mesh>

      {/* Mousse/lichen sur la pierre */}
      <mesh position={[-0.25, 0.35, 0.25]} castShadow>
        <sphereGeometry args={[0.06, 6, 6]} />
        <meshStandardMaterial color="#3a5a3a" roughness={1} />
      </mesh>
      <mesh position={[0.2, 0.25, -0.3]} castShadow>
        <sphereGeometry args={[0.05, 6, 6]} />
        <meshStandardMaterial color="#3a5a3a" roughness={1} />
      </mesh>

      {/* Lueur mystique quand on survole */}
      {hovered && (
        <>
          <pointLight
            position={[0, 0.8, 0.5]}
            intensity={1}
            color={runeColor}
            distance={3}
          />
          {/* Aura au sol */}
          <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[0.5, 0.7, 32]} />
            <meshBasicMaterial color={runeColor} transparent opacity={0.3} />
          </mesh>
        </>
      )}
    </group>
  );
}

// ==================== FONTAINE MAGIQUE ANIMÉE ====================

function FountainModel({ world, hovered }: ModelProps) {
  const isSpectral = world === 'spectral';
  const stoneColor = isSpectral ? '#4a4a5a' : '#8b7355';
  const waterColor = isSpectral ? '#9370db' : '#87ceeb';
  const glowIntensity = hovered ? 0.8 : 0.4;

  // Refs pour l'animation
  const groupRef = useRef<Group>(null);
  const waterSurfaceRef = useRef<Mesh>(null);
  const jetRef = useRef<Mesh>(null);
  const dropletsRef = useRef<(Mesh | null)[]>([]);

  // Animation de la fontaine
  useFrame((state) => {
    const time = state.clock.elapsedTime;

    // Animation de la surface de l'eau (ondulations)
    if (waterSurfaceRef.current) {
      waterSurfaceRef.current.scale.x = 1 + Math.sin(time * 2) * 0.02;
      waterSurfaceRef.current.scale.z = 1 + Math.cos(time * 2) * 0.02;
    }

    // Animation du jet central (pulsation)
    if (jetRef.current) {
      jetRef.current.scale.y = 1 + Math.sin(time * 4) * 0.15;
      jetRef.current.position.y = 1.15 + Math.sin(time * 4) * 0.05;
    }

    // Animation des gouttes d'eau (chute cyclique)
    dropletsRef.current.forEach((droplet, i) => {
      if (droplet) {
        // Chaque goutte a un timing différent
        const dropTime = (time * 2 + i * 0.5) % 1.5; // Cycle de 1.5 secondes
        const startY = 1.0;
        const endY = 0.3;
        const progress = dropTime / 1.5;

        // Position Y: descente avec courbe légère
        droplet.position.y = startY - (startY - endY) * progress;

        // Position X/Z: arc vers l'extérieur
        const angle = (i / 8) * Math.PI * 2 + time * 0.5;
        const radius = 0.15 + progress * 0.35;
        droplet.position.x = Math.sin(angle) * radius;
        droplet.position.z = Math.cos(angle) * radius;

        // Taille: grandit légèrement puis rapetisse
        const sizeMod = Math.sin(progress * Math.PI) * 0.5 + 0.5;
        droplet.scale.setScalar(0.8 + sizeMod * 0.4);

        // Opacité: fade out vers la fin
        const material = droplet.material as THREE.MeshStandardMaterial;
        if (material && 'opacity' in material) {
          material.opacity = 0.7 * (1 - progress * 0.5);
        }
      }
    });
  });

  return (
    <group ref={groupRef}>
      {/* Base circulaire de la fontaine */}
      <mesh position={[0, 0.15, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[1.2, 1.4, 0.3, 16]} />
        <meshStandardMaterial color={stoneColor} roughness={0.8} />
      </mesh>

      {/* Rebord de la fontaine */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <torusGeometry args={[1.1, 0.1, 8, 24]} />
        <meshStandardMaterial color={stoneColor} roughness={0.7} />
      </mesh>

      {/* Eau dans le bassin (animée) */}
      <mesh ref={waterSurfaceRef} position={[0, 0.25, 0]}>
        <cylinderGeometry args={[1.0, 1.0, 0.15, 24]} />
        <meshStandardMaterial
          color={waterColor}
          transparent
          opacity={0.7}
          emissive={waterColor}
          emissiveIntensity={glowIntensity * 0.3}
          roughness={0.1}
          metalness={0.3}
        />
      </mesh>

      {/* Pilier central */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.2, 0.8, 8]} />
        <meshStandardMaterial color={stoneColor} roughness={0.7} />
      </mesh>

      {/* Vasque supérieure */}
      <mesh position={[0, 1.0, 0]} castShadow>
        <cylinderGeometry args={[0.4, 0.3, 0.15, 12]} />
        <meshStandardMaterial color={stoneColor} roughness={0.7} />
      </mesh>

      {/* Jet d'eau central animé */}
      <mesh ref={jetRef} position={[0, 1.15, 0]}>
        <sphereGeometry args={[0.12, 12, 12]} />
        <meshStandardMaterial
          color={waterColor}
          transparent
          opacity={0.8}
          emissive={waterColor}
          emissiveIntensity={glowIntensity}
        />
      </mesh>

      {/* Gouttes d'eau tombantes animées */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <mesh
          key={i}
          ref={(el) => { dropletsRef.current[i] = el; }}
          position={[0, 0.7, 0]}
        >
          <sphereGeometry args={[0.035, 8, 8]} />
          <meshStandardMaterial
            color={waterColor}
            transparent
            opacity={0.7}
            emissive={waterColor}
            emissiveIntensity={glowIntensity * 0.5}
          />
        </mesh>
      ))}

      {/* Ondulations à la surface (cercles concentriques) */}
      {[0, 1, 2].map((i) => (
        <mesh key={`ripple-${i}`} position={[0, 0.33, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.3 + i * 0.25, 0.32 + i * 0.25, 32]} />
          <meshStandardMaterial
            color={waterColor}
            transparent
            opacity={0.3 - i * 0.08}
            emissive={waterColor}
            emissiveIntensity={glowIntensity * 0.2}
          />
        </mesh>
      ))}

      {/* Lumière ambiante de la fontaine */}
      <pointLight
        position={[0, 0.5, 0]}
        intensity={isSpectral ? 1.5 : 0.8}
        color={waterColor}
        distance={4}
      />

      {/* Ornements sur le rebord */}
      {[0, 1, 2, 3].map((i) => (
        <mesh key={i} position={[
          Math.sin(i * Math.PI / 2) * 1.1,
          0.5,
          Math.cos(i * Math.PI / 2) * 1.1
        ]} castShadow>
          <sphereGeometry args={[0.08, 8, 8]} />
          <meshStandardMaterial
            color={isSpectral ? '#9370db' : '#daa520'}
            emissive={isSpectral ? '#9370db' : '#daa520'}
            emissiveIntensity={glowIntensity * 0.5}
          />
        </mesh>
      ))}
    </group>
  );
}

// ==================== RUNES LUMINEUSES AU SOL ====================

function RuneCircleModel({ world, hovered }: ModelProps) {
  const isSpectral = world === 'spectral';
  const runeColor = isSpectral ? '#9370db' : '#daa520';
  const secondaryColor = isSpectral ? '#6a5acd' : '#ffd700';
  const glowIntensity = hovered ? 1.5 : 0.8;

  return (
    <group rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
      {/* Aura de fond diffuse */}
      <mesh position={[0, 0, -0.01]}>
        <circleGeometry args={[1.15, 32]} />
        <meshBasicMaterial
          color={runeColor}
          transparent
          opacity={0.15}
        />
      </mesh>

      {/* Cercle extérieur principal */}
      <mesh>
        <ringGeometry args={[0.95, 1.05, 48]} />
        <meshStandardMaterial
          color={runeColor}
          emissive={runeColor}
          emissiveIntensity={glowIntensity}
          transparent
          opacity={0.9}
        />
      </mesh>

      {/* Cercle extérieur secondaire (double ligne) */}
      <mesh position={[0, 0, 0.005]}>
        <ringGeometry args={[0.85, 0.88, 48]} />
        <meshStandardMaterial
          color={secondaryColor}
          emissive={secondaryColor}
          emissiveIntensity={glowIntensity * 0.7}
          transparent
          opacity={0.7}
        />
      </mesh>

      {/* Cercle intérieur principal */}
      <mesh position={[0, 0, 0.01]}>
        <ringGeometry args={[0.48, 0.55, 48]} />
        <meshStandardMaterial
          color={runeColor}
          emissive={runeColor}
          emissiveIntensity={glowIntensity}
          transparent
          opacity={0.85}
        />
      </mesh>

      {/* Cercle intérieur secondaire */}
      <mesh position={[0, 0, 0.015]}>
        <ringGeometry args={[0.4, 0.43, 48]} />
        <meshStandardMaterial
          color={secondaryColor}
          emissive={secondaryColor}
          emissiveIntensity={glowIntensity * 0.6}
          transparent
          opacity={0.6}
        />
      </mesh>

      {/* Lignes radiales reliant les cercles */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const angle = (i / 8) * Math.PI * 2;
        const innerRadius = 0.55;
        const outerRadius = 0.85;
        const midX = Math.cos(angle) * ((innerRadius + outerRadius) / 2);
        const midY = Math.sin(angle) * ((innerRadius + outerRadius) / 2);
        return (
          <mesh key={`radial-${i}`} position={[midX, midY, 0.02]} rotation={[0, 0, angle + Math.PI / 2]}>
            <boxGeometry args={[0.03, outerRadius - innerRadius, 0.01]} />
            <meshStandardMaterial
              color={runeColor}
              emissive={runeColor}
              emissiveIntensity={glowIntensity * 0.9}
            />
          </mesh>
        );
      })}

      {/* Symboles runiques complexes (formes variées) */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const angle = (i / 6) * Math.PI * 2 + Math.PI / 12;
        const radius = 0.7;
        return (
          <group key={`rune-${i}`} position={[
            Math.cos(angle) * radius,
            Math.sin(angle) * radius,
            0.02
          ]} rotation={[0, 0, angle]}>
            {/* Symbole principal */}
            <mesh>
              <boxGeometry args={[0.12, 0.03, 0.01]} />
              <meshStandardMaterial
                color={secondaryColor}
                emissive={secondaryColor}
                emissiveIntensity={glowIntensity * 1.2}
              />
            </mesh>
            {/* Trait vertical */}
            <mesh position={[0, 0.04, 0]}>
              <boxGeometry args={[0.02, 0.06, 0.01]} />
              <meshStandardMaterial
                color={runeColor}
                emissive={runeColor}
                emissiveIntensity={glowIntensity}
              />
            </mesh>
            {/* Points décoratifs */}
            <mesh position={[-0.06, 0, 0.005]}>
              <circleGeometry args={[0.015, 6]} />
              <meshStandardMaterial
                color={runeColor}
                emissive={runeColor}
                emissiveIntensity={glowIntensity * 1.5}
              />
            </mesh>
            <mesh position={[0.06, 0, 0.005]}>
              <circleGeometry args={[0.015, 6]} />
              <meshStandardMaterial
                color={runeColor}
                emissive={runeColor}
                emissiveIntensity={glowIntensity * 1.5}
              />
            </mesh>
          </group>
        );
      })}

      {/* Points lumineux aux intersections du cercle extérieur */}
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => {
        const angle = (i / 12) * Math.PI * 2;
        return (
          <mesh key={`point-outer-${i}`} position={[
            Math.cos(angle) * 1.0,
            Math.sin(angle) * 1.0,
            0.025
          ]}>
            <circleGeometry args={[0.035, 8]} />
            <meshStandardMaterial
              color={secondaryColor}
              emissive={secondaryColor}
              emissiveIntensity={glowIntensity * 1.5}
            />
          </mesh>
        );
      })}

      {/* Symbole central - pentagramme stylisé */}
      <mesh position={[0, 0, 0.02]}>
        <circleGeometry args={[0.18, 5]} />
        <meshStandardMaterial
          color={runeColor}
          emissive={runeColor}
          emissiveIntensity={glowIntensity * 1.8}
        />
      </mesh>

      {/* Centre brillant */}
      <mesh position={[0, 0, 0.025]}>
        <circleGeometry args={[0.08, 16]} />
        <meshStandardMaterial
          color={secondaryColor}
          emissive={secondaryColor}
          emissiveIntensity={glowIntensity * 2.5}
        />
      </mesh>

      {/* Rayons du centre vers l'intérieur */}
      {[0, 1, 2, 3, 4].map((i) => {
        const angle = (i / 5) * Math.PI * 2;
        return (
          <mesh key={`ray-${i}`} position={[
            Math.cos(angle) * 0.28,
            Math.sin(angle) * 0.28,
            0.022
          ]} rotation={[0, 0, angle + Math.PI / 2]}>
            <boxGeometry args={[0.02, 0.2, 0.01]} />
            <meshStandardMaterial
              color={runeColor}
              emissive={runeColor}
              emissiveIntensity={glowIntensity * 1.3}
            />
          </mesh>
        );
      })}

      {/* Lumière ambiante puissante */}
      <pointLight
        position={[0, 0, 0.8]}
        intensity={glowIntensity * 4}
        color={runeColor}
        distance={5}
      />

      {/* Lumière secondaire pour l'effet volumétrique */}
      <pointLight
        position={[0, 0, 0.3]}
        intensity={glowIntensity * 2}
        color={secondaryColor}
        distance={3}
      />
    </group>
  );
}

// ==================== ARCHE DÉCORATIVE ====================

function ArchModel({ world, hovered }: ModelProps) {
  const isSpectral = world === 'spectral';
  const stoneColor = isSpectral ? '#4a4a5a' : '#8b7355';
  const accentColor = isSpectral ? '#9370db' : '#daa520';
  const glowIntensity = hovered ? 0.8 : 0.3;

  return (
    <group>
      {/* Pilier gauche */}
      <mesh position={[-0.8, 1, 0]} castShadow>
        <boxGeometry args={[0.3, 2, 0.3]} />
        <meshStandardMaterial color={stoneColor} roughness={0.8} />
      </mesh>

      {/* Pilier droit */}
      <mesh position={[0.8, 1, 0]} castShadow>
        <boxGeometry args={[0.3, 2, 0.3]} />
        <meshStandardMaterial color={stoneColor} roughness={0.8} />
      </mesh>

      {/* Arc supérieur */}
      <mesh position={[0, 2.2, 0]} rotation={[Math.PI / 2, 0, 0]} castShadow>
        <torusGeometry args={[0.8, 0.15, 8, 16, Math.PI]} />
        <meshStandardMaterial color={stoneColor} roughness={0.8} />
      </mesh>

      {/* Ornement central sur l'arc */}
      <mesh position={[0, 2.95, 0]} castShadow>
        <octahedronGeometry args={[0.15, 0]} />
        <meshStandardMaterial
          color={accentColor}
          emissive={accentColor}
          emissiveIntensity={glowIntensity}
        />
      </mesh>

      {/* Orbes lumineux sur les piliers */}
      <mesh position={[-0.8, 2.1, 0.2]}>
        <sphereGeometry args={[0.08, 8, 8]} />
        <meshStandardMaterial
          color={accentColor}
          emissive={accentColor}
          emissiveIntensity={glowIntensity * 1.5}
        />
      </mesh>
      <mesh position={[0.8, 2.1, 0.2]}>
        <sphereGeometry args={[0.08, 8, 8]} />
        <meshStandardMaterial
          color={accentColor}
          emissive={accentColor}
          emissiveIntensity={glowIntensity * 1.5}
        />
      </mesh>

      {/* Bases décoratives */}
      <mesh position={[-0.8, 0.1, 0]} castShadow>
        <boxGeometry args={[0.4, 0.2, 0.4]} />
        <meshStandardMaterial color={stoneColor} roughness={0.7} />
      </mesh>
      <mesh position={[0.8, 0.1, 0]} castShadow>
        <boxGeometry args={[0.4, 0.2, 0.4]} />
        <meshStandardMaterial color={stoneColor} roughness={0.7} />
      </mesh>

      {/* Lumière sous l'arche */}
      <pointLight
        position={[0, 1.5, 0]}
        intensity={glowIntensity * 3}
        color={accentColor}
        distance={4}
      />
    </group>
  );
}

// ==================== SPHÈRE RUNIQUE PORTAIL ====================

/**
 * Sphère Runique au Sol - Portail de téléportation
 * Plus facile à cliquer qu'un portail vertical
 * Rose/brillant quand actif, gris quand inactif
 * Animation de transition quand il s'active
 */
function RunicSpherePortalModel({ world, hovered, properties }: ModelProps) {
  const isSpectral = world === 'spectral';
  const isActive = properties.isActive as boolean;
  const portalColor = (properties.portalColor as string) || '#ff69b4';
  const inactiveColor = '#555555';

  // Animation state
  const groupRef = useRef<Group>(null);
  const activationProgress = useRef(isActive ? 1 : 0);
  const wasActive = useRef(isActive);
  const activationBurst = useRef(0); // For burst animation when activating

  // Animate the transition
  useFrame((state, delta) => {
    const targetProgress = isActive ? 1 : 0;

    // Detect activation moment
    if (isActive && !wasActive.current) {
      activationBurst.current = 1; // Start burst animation
    }
    wasActive.current = isActive;

    // Smooth transition for activation
    activationProgress.current += (targetProgress - activationProgress.current) * Math.min(delta * 3, 1);

    // Decay burst effect
    if (activationBurst.current > 0) {
      activationBurst.current -= delta * 0.8;
      if (activationBurst.current < 0) activationBurst.current = 0;
    }

    // Rotate the group slightly when active
    if (groupRef.current && isActive) {
      groupRef.current.rotation.y += delta * 0.3;
    }
  });

  // Interpolate colors based on activation progress
  const progress = activationProgress.current;
  const burst = activationBurst.current;

  // Parse colors for interpolation
  const activeR = parseInt(portalColor.slice(1, 3), 16) / 255;
  const activeG = parseInt(portalColor.slice(3, 5), 16) / 255;
  const activeB = parseInt(portalColor.slice(5, 7), 16) / 255;
  const inactiveR = 0.33, inactiveG = 0.33, inactiveB = 0.33;

  const r = inactiveR + (activeR - inactiveR) * progress;
  const g = inactiveG + (activeG - inactiveG) * progress;
  const b = inactiveB + (activeB - inactiveB) * progress;

  const color = `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`;
  const glowIntensity = 0.2 + progress * (hovered ? 1.8 : 1.0) + burst * 2;
  const secondaryColor = progress > 0.5 ? '#ffffff' : '#333333';
  const sphereOpacity = 0.4 + progress * 0.5;

  return (
    <group ref={groupRef}>
      {/* Hitbox invisible pour faciliter le clic */}
      <mesh position={[0, 0.4, 0]}>
        <sphereGeometry args={[0.8, 16, 16]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* Base runique au sol */}
      <group rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        {/* Cercle de base */}
        <mesh>
          <ringGeometry args={[0.7, 0.8, 32]} />
          <meshStandardMaterial
            color={color}
            emissive={color}
            emissiveIntensity={glowIntensity * 0.5}
            transparent
            opacity={0.9}
          />
        </mesh>

        {/* Cercle intérieur */}
        <mesh position={[0, 0, 0.01]}>
          <ringGeometry args={[0.4, 0.5, 32]} />
          <meshStandardMaterial
            color={color}
            emissive={color}
            emissiveIntensity={glowIntensity * 0.6}
            transparent
            opacity={0.8}
          />
        </mesh>

        {/* Runes autour */}
        {[0, 1, 2, 3, 4, 5].map((i) => {
          const angle = (i / 6) * Math.PI * 2;
          return (
            <mesh key={`rune-${i}`} position={[
              Math.cos(angle) * 0.6,
              Math.sin(angle) * 0.6,
              0.02
            ]}>
              <circleGeometry args={[0.06, 6]} />
              <meshStandardMaterial
                color={secondaryColor}
                emissive={color}
                emissiveIntensity={glowIntensity * 0.8}
              />
            </mesh>
          );
        })}
      </group>

      {/* Sphère principale flottante */}
      <mesh position={[0, 0.5, 0]} castShadow>
        <sphereGeometry args={[0.35, 32, 32]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={glowIntensity}
          transparent
          opacity={sphereOpacity}
          roughness={0.1}
          metalness={0.3}
        />
      </mesh>

      {/* Noyau brillant */}
      <mesh position={[0, 0.5, 0]}>
        <sphereGeometry args={[0.15, 16, 16]} />
        <meshStandardMaterial
          color={secondaryColor}
          emissive={color}
          emissiveIntensity={glowIntensity * 1.5}
          transparent
          opacity={0.3 + progress * 0.7}
        />
      </mesh>

      {/* Burst effect - cercle expansif lors de l'activation */}
      {burst > 0.01 && (
        <mesh position={[0, 0.5, 0]}>
          <sphereGeometry args={[0.35 + burst * 1.5, 16, 16]} />
          <meshBasicMaterial
            color={portalColor}
            transparent
            opacity={burst * 0.6}
          />
        </mesh>
      )}

      {/* Anneaux orbitaux (apparaissent progressivement) */}
      {progress > 0.3 && (
        <>
          <mesh position={[0, 0.5, 0]} rotation={[Math.PI / 3, 0, 0]}>
            <torusGeometry args={[0.45, 0.02, 8, 32]} />
            <meshStandardMaterial
              color={color}
              emissive={color}
              emissiveIntensity={glowIntensity}
              transparent
              opacity={0.7}
            />
          </mesh>
          <mesh position={[0, 0.5, 0]} rotation={[-Math.PI / 4, Math.PI / 3, 0]}>
            <torusGeometry args={[0.5, 0.015, 8, 32]} />
            <meshStandardMaterial
              color={secondaryColor}
              emissive={color}
              emissiveIntensity={glowIntensity * 0.8}
              transparent
              opacity={0.6}
            />
          </mesh>
        </>
      )}

      {/* Particules flottantes autour (si actif) */}
      {isActive && [0, 1, 2, 3, 4, 5].map((i) => {
        const angle = (i / 6) * Math.PI * 2;
        const height = 0.3 + (i % 3) * 0.2;
        return (
          <mesh key={`particle-${i}`} position={[
            Math.cos(angle) * 0.55,
            height + 0.3,
            Math.sin(angle) * 0.55
          ]}>
            <sphereGeometry args={[0.03, 8, 8]} />
            <meshStandardMaterial
              color={color}
              emissive={color}
              emissiveIntensity={glowIntensity * 1.2}
              transparent
              opacity={0.8}
            />
          </mesh>
        );
      })}

      {/* Lumière de la sphère */}
      <pointLight
        position={[0, 0.5, 0]}
        intensity={isActive ? glowIntensity * 3 : 0.5}
        color={color}
        distance={isActive ? 5 : 2}
      />

      {/* Lumière au sol */}
      <pointLight
        position={[0, 0.1, 0]}
        intensity={isActive ? glowIntensity * 1.5 : 0.2}
        color={color}
        distance={3}
      />

      {/* Effet de hover quand actif */}
      {hovered && isActive && (
        <mesh position={[0, 0.5, 0]}>
          <sphereGeometry args={[0.6, 16, 16]} />
          <meshBasicMaterial
            color={color}
            transparent
            opacity={0.15}
          />
        </mesh>
      )}
    </group>
  );
}

// ==================== EXPORT REGISTRY ====================

export const reveilEducatriceModels: ModelRegistry = {
  oracle_chevelu: OracleCheveluModel,
  ancient_stone: AncientStoneModel,
  // Portails thématiques - ancien style vertical
  portal_wonderland: PortalModel,
  portal_hawkins: PortalModel,
  portal_jurassic: PortalModel,
  portal_winterfell: PortalModel,
  portal_middleearth: PortalModel,
  // Nouveau style - sphères runiques au sol
  runic_sphere_portal: RunicSpherePortalModel,
  // Éléments décoratifs du sanctuaire
  pillar: PillarModel,
  orb: OrbModel,
  crystal: CrystalModel,
  platform_crystal: PlatformCrystalModel,
  cushion_gold: CushionGoldModel,
  // Nouveaux éléments atmosphériques
  fountain: FountainModel,
  rune_circle: RuneCircleModel,
  arch: ArchModel,
};
